import { lang } from "../../i18nLang";
import { formatCode } from "../../utils";
import { NotFoundException } from "../exceptions/NotFoundException";
import { IAdvanceLink, IDiscountLink } from "../interfaces";
import MeasurementModel from "../models/MeasurementModel";
import MeasurementService from "../services/MeasurementService";
import MegaERPService from "../services/MegaERPService";

class MeasurementController {
  async createMeasurement(contractUid): Promise<string> {
    const code = await MeasurementService.getNextCode(contractUid);
    const measurement = MeasurementModel.createBlankMeasurement(
      contractUid,
      code,
      lang("measurements.defaultName", formatCode(code))
    );

    return MeasurementService.createMeasurement(measurement);
  }

  async linkDiscountsToMeasurement(
    transaction,
    measurement: MeasurementModel,
    discountsLinks: IDiscountLink[],
    billingsLinks: IDiscountLink[]
  ): Promise<void> {
    await MeasurementService.updateMeasurementDiscountsWithTransaction(
      transaction,
      measurement,
      discountsLinks
    );

    await MeasurementService.updateMeasurementBillingsWithTransaction(
      transaction,
      measurement,
      billingsLinks
    );
  }

  async linkAdvancesToMeasurement(
    transaction,
    measurement: MeasurementModel,
    advancesLinks: IAdvanceLink[]
  ): Promise<void> {
    await MeasurementService.updateMeasurementAdvancesWithTransaction(
      transaction,
      measurement,
      advancesLinks
    );
  }

  async updateCurrentBillingOrderStatus(
    api: MegaERPService,
    measurement: MeasurementModel
  ): Promise<void> {
    if (!measurement.hasBeenSentToBilling()) return;
    if (measurement.hasBillingOrder() || measurement.hasBillingOrderError())
      return;

    const billingOrder = { ...measurement.billingOrder };
    const data = await api.getBillingOrder(billingOrder.sequencial);
    const newBillingOrder = {
      ...billingOrder,
      ...data,
    };

    await MeasurementService.updateMeasurementBillingOrder(
      measurement,
      newBillingOrder
    );
    measurement.billingOrder = newBillingOrder;
  }

  async updateBillingOrderInvoices(
    api: MegaERPService,
    measurement: MeasurementModel
  ): Promise<void> {
    if (!measurement.hasBillingOrder()) return;
    if (measurement.hasBillingOrderError()) return;

    const billingOrder = { ...measurement.billingOrder };

    try {
      const invoices = await api.getOrderInvoices(
        billingOrder.branchId,
        billingOrder.code,
        billingOrder.serial
      );

      const isSame =
        invoices.length === billingOrder.invoices.length &&
        invoices.every(isEqual);
      if (isSame) return;

      return MeasurementService.updateMeasurementBillingOrder(measurement, {
        ...billingOrder,
        invoices,
      });
    } catch (error) {
      if (error instanceof NotFoundException)
        return MeasurementService.updateBillingOrderToDeleted(measurement);
    }

    function isEqual(invoice, index) {
      return (
        invoice.number === billingOrder.invoices[index].number &&
        invoice.serial === billingOrder.invoices[index].serial &&
        invoice.issuanceDate === billingOrder.invoices[index].issuanceDate
      );
    }
  }
}

export default new MeasurementController();
